import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { productsApi } from "../../../../api/products";
import { formatApiError } from "../../../../api/client";
import ImportSourceStep from "./ImportSourceStep";
import ImportSampleStep from "./ImportSampleStep";
import ImportMappingStep from "./ImportMappingStep";
import ImportDefaultsStep from "./ImportDefaultsStep";
import ImportPreviewStep from "./ImportPreviewStep";
import ImportResultStep from "./ImportResultStep";
import { mappedTarget, suggestImportMapping } from "./importFields";

const STEPS = ["Fuente", "Muestra", "Mapping", "Defaults y modo", "Preview", "Confirmación"];
const INITIAL_DEFAULTS = {
  category: "General",
  unit: "",
  itemKind: "product",
  active: true,
  publicVisible: true,
  sellable: true,
  purchasable: true,
};

export default function ProductImportWizard({ open, projectId, onClose }) {
  const fullScreen = useMediaQuery("(max-width:699.95px)", { noSsr: true });
  const [step, setStep] = React.useState(0);
  const [sourceMode, setSourceMode] = React.useState("file");
  const [file, setFile] = React.useState(null);
  const [googleUrl, setGoogleUrl] = React.useState("");
  const [draft, setDraft] = React.useState(null);
  const [mapping, setMapping] = React.useState({});
  const [defaults, setDefaults] = React.useState(INITIAL_DEFAULTS);
  const [mode, setMode] = React.useState("create_only");
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const importIdRef = React.useRef("");
  const projectIdRef = React.useRef(projectId);

  React.useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  const resetState = React.useCallback(() => {
    setStep(0);
    setSourceMode("file");
    setFile(null);
    setGoogleUrl("");
    setDraft(null);
    setMapping({});
    setDefaults(INITIAL_DEFAULTS);
    setMode("create_only");
    setPreview(null);
    setBusy(false);
    setError("");
  }, []);

  const discardDraft = React.useCallback(async () => {
    const importId = importIdRef.current;
    importIdRef.current = "";
    if (!importId || !projectIdRef.current) return;
    try {
      await productsApi.deleteImport(projectIdRef.current, importId);
    } catch {
      // Best effort: el draft además expira por TTL en backend.
    }
  }, []);

  React.useEffect(() => () => {
    const importId = importIdRef.current;
    const currentProject = projectIdRef.current;
    if (importId && currentProject) {
      void productsApi.deleteImport(currentProject, importId).catch(() => {});
      importIdRef.current = "";
    }
  }, []);

  const handleClose = React.useCallback(async () => {
    if (busy) return;
    await discardDraft();
    resetState();
    onClose?.();
  }, [busy, discardDraft, onClose, resetState]);

  const createDraft = async () => {
    setBusy(true);
    setError("");
    try {
      await discardDraft();
      const response = await productsApi.createImport(
        projectId,
        sourceMode === "file" ? { file } : { url: googleUrl },
      );
      importIdRef.current = response.importId;
      setDraft(response);
      setFile(null);
      setMapping(suggestImportMapping(response.headers || []));
      setPreview(null);
      setStep(1);
    } catch (err) {
      setError(formatApiError(err, "No se pudo preparar la importación"));
    } finally {
      setBusy(false);
    }
  };

  const mappingHasName = Boolean(mappedTarget(mapping, "name"));
  const codeMapped = Boolean(mappedTarget(mapping, "code"));

  React.useEffect(() => {
    if (!codeMapped && mode === "upsert_by_code") setMode("create_only");
  }, [codeMapped, mode]);

  const generatePreview = async () => {
    if (!draft?.importId || !mappingHasName) return;
    setBusy(true);
    setError("");
    try {
      const response = await productsApi.previewImport(projectId, draft.importId, {
        mapping,
        defaults,
        mode,
      });
      setPreview(response);
      setStep(4);
    } catch (err) {
      setError(formatApiError(err, "No se pudo generar la vista previa"));
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => {
    setError("");
    setStep((current) => Math.max(0, current - 1));
  };

  const goForward = async () => {
    setError("");
    if (step === 1) setStep(2);
    else if (step === 2 && mappingHasName) setStep(3);
    else if (step === 3) await generatePreview();
    else if (step === 4 && preview) setStep(5);
  };

  const renderStep = () => {
    if (step === 0) {
      return (
        <ImportSourceStep
          sourceMode={sourceMode}
          onSourceModeChange={setSourceMode}
          file={file}
          onFileChange={setFile}
          googleUrl={googleUrl}
          onGoogleUrlChange={setGoogleUrl}
          busy={busy}
          onContinue={createDraft}
        />
      );
    }
    if (step === 1) return <ImportSampleStep draft={draft} />;
    if (step === 2) {
      return (
        <ImportMappingStep
          headers={draft?.headers || []}
          mapping={mapping}
          onChange={(source, target) => setMapping((current) => ({ ...current, [source]: target }))}
        />
      );
    }
    if (step === 3) {
      return (
        <ImportDefaultsStep
          defaults={defaults}
          onDefaultsChange={(field, value) => setDefaults((current) => ({ ...current, [field]: value }))}
          mode={mode}
          onModeChange={setMode}
          codeMapped={codeMapped}
        />
      );
    }
    if (step === 4) return <ImportPreviewStep preview={preview} />;
    return <ImportResultStep preview={preview} />;
  };

  const showFooterNext = step > 0 && step < 5;
  const footerNextDisabled = busy || (step === 2 && !mappingHasName) || (step === 4 && !preview);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { minHeight: fullScreen ? "100dvh" : "min(820px, calc(100dvh - 48px))" } }}
    >
      <DialogTitle sx={{ pr: 7, pb: 1.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>IMPORTADOR DE ÍTEMS</Typography>
        <Typography variant="h5" sx={{ fontWeight: 950 }}>Importar ítems</Typography>
        <IconButton
          aria-label="Cerrar importador"
          onClick={handleClose}
          disabled={busy}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      {busy ? <LinearProgress /> : null}

      <DialogContent dividers sx={{ bgcolor: "#F7F9FB", p: { xs: 1.5, sm: 2.5 } }}>
        <Stack spacing={2.5}>
          <Box sx={{ overflowX: "auto", pb: 0.5 }}>
            <Stepper activeStep={step} sx={{ minWidth: 720 }}>
              {STEPS.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {busy && !error ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Procesando…</Typography>
            </Stack>
          ) : null}

          <Box sx={{ bgcolor: "background.paper", borderRadius: 3, p: { xs: 1.5, sm: 2.5 }, border: "1px solid", borderColor: "divider" }}>
            {renderStep()}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.75, justifyContent: "space-between" }}>
        <Button onClick={handleClose} disabled={busy} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Stack direction="row" spacing={1}>
          {step > 0 ? (
            <Button startIcon={<ArrowBackRoundedIcon />} onClick={goBack} disabled={busy} sx={{ textTransform: "none" }}>
              Atrás
            </Button>
          ) : null}
          {showFooterNext ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={goForward}
              disabled={footerNextDisabled}
              sx={{ textTransform: "none", fontWeight: 900 }}
            >
              {step === 3 ? "Generar preview" : step === 4 ? "Revisar confirmación" : "Continuar"}
            </Button>
          ) : null}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
